import { useEffect, useRef, useState } from "react";
import { useAnimateOnScroll } from "@/hooks/useAnimateOnScroll";

interface AnimatedCounterProps {
  value: string; // formatted value like "$45.2M", "1,234", "12.5K"
  className?: string;
  style?: React.CSSProperties;
  duration?: number; // ms, default 2000
}

/**
 * Parses a formatted string and extracts the numeric portion + prefix/suffix.
 * Examples: "$45.2M" → { prefix: "$", num: 45.2, suffix: "M", decimals: 1 }
 *           "1,234" → { prefix: "", num: 1234, suffix: "", decimals: 0 }
 */
function parseValue(str: string) {
  // Extract leading non-numeric chars (prefix like $)
  const prefixMatch = str.match(/^([^0-9]*)/);
  const prefix = prefixMatch ? prefixMatch[1] : "";

  // Extract trailing non-numeric chars (suffix like M, K, %)
  const suffixMatch = str.match(/([^0-9.]*)$/);
  const suffix = suffixMatch ? suffixMatch[1] : "";

  // Extract the number in the middle
  const numStr = str.slice(prefix.length, str.length - suffix.length).replace(/,/g, "");
  const num = parseFloat(numStr) || 0;
  const decimalPart = numStr.split(".")[1];
  const decimals = decimalPart ? decimalPart.length : 0;

  return { prefix, num, suffix, decimals };
}

function formatWithCommas(n: number, decimals: number): string {
  const fixed = n.toFixed(decimals);
  const [int, dec] = fixed.split(".");
  const withCommas = int.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return dec !== undefined ? `${withCommas}.${dec}` : withCommas;
}

export function AnimatedCounter({ value, className, style, duration = 2000 }: AnimatedCounterProps) {
  const { ref, isVisible } = useAnimateOnScroll(0.15);
  const [displayValue, setDisplayValue] = useState("0");
  const animationRef = useRef<number | null>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!isVisible || hasAnimated.current) return;
    hasAnimated.current = true;

    const { prefix, num, suffix, decimals } = parseValue(value);
    const startTime = performance.now();

    const animate = (now: number) => {
      const elapsed = now - startTime;
      const rawProgress = Math.min(elapsed / duration, 1);
      // Ease-out cubic for Apple-like deceleration
      const progress = 1 - Math.pow(1 - rawProgress, 3);

      const current = num * progress;
      setDisplayValue(`${prefix}${formatWithCommas(current, decimals)}${suffix}`);

      if (rawProgress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setDisplayValue(value); // Ensure exact final value
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isVisible, value, duration]);

  return (
    <span ref={ref} className={className} style={style}>
      {isVisible ? displayValue : "\u00A0"} {/* nbsp placeholder to prevent layout shift */}
    </span>
  );
}
